import React from "react";
import { Plug, RefreshCw, Link2, Import, Workflow, KeyRound } from "lucide-react";
import FeaturePage, { MockWindow } from "../features/FeaturePage";
import { siblingSolutions, DEMO_CTA } from "./links";

const ACCENT = "#0EA5E9";

const ATS = [
  { name: "Greenhouse", how: "Harvest API key", live: true },
  { name: "Lever", how: "API key", live: true },
  { name: "Workday", how: "URL import", live: true },
];

// ── Hero visual: connected ATS ──
function AtsVisual() {
  return (
    <MockWindow label="ATS INTEGRATIONS" badge="Connected" accent={ACCENT}>
      <div className="space-y-2.5">
        {ATS.map((a) => (
          <div key={a.name} className="flex items-center gap-3 rounded-xl border border-border p-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${ACCENT}14`, color: ACCENT }}>
              <Plug className="w-4 h-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-sm text-[#0F172A]">{a.name}</div>
              <div className="text-[11px] text-muted-foreground">{a.how}</div>
            </div>
            <span className="flex items-center gap-1 font-mono text-[9px]" style={{ color: "#047857" }}>
              <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" /> live
            </span>
          </div>
        ))}
      </div>
    </MockWindow>
  );
}

// ── Row visual: sync candidates ──
function SyncVisual() {
  return (
    <MockWindow label="SYNC · GREENHOUSE" accent={ACCENT}>
      <div className="space-y-3">
        <div className="flex items-center justify-center gap-2 font-mono text-[10px]" style={{ color: ACCENT }}>
          <RefreshCw className="w-3.5 h-3.5" /> Pulled 34 candidates
        </div>
        <div className="space-y-2">
          {["A. Okafor", "M. Chen", "R. Patel"].map((n, i) => (
            <div key={n} className="flex items-center gap-3 rounded-xl border border-border p-2.5">
              <Import className="w-3.5 h-3.5 flex-shrink-0" style={{ color: ACCENT }} />
              <span className="text-xs text-[#0F172A] flex-1">{n}</span>
              <span className="font-mono text-[9px] text-muted-foreground">imported</span>
            </div>
          ))}
        </div>
      </div>
    </MockWindow>
  );
}

// ── Row visual: connect with a key ──
function ConnectVisual() {
  return (
    <MockWindow label="CONNECT AN ATS" accent={ACCENT}>
      <div className="space-y-3">
        <div>
          <div className="font-mono text-[10px] text-muted-foreground tracking-[0.15em] mb-1.5">HARVEST API KEY</div>
          <div className="rounded-xl border border-border p-3 flex items-center gap-2 bg-secondary/30">
            <KeyRound className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="font-mono text-xs text-muted-foreground tracking-wider">••••••••••••••••••••</span>
          </div>
        </div>
        <div className="flex items-center justify-center gap-1.5 rounded-xl px-3 py-2.5 text-xs font-bold text-white" style={{ background: ACCENT }}>
          <Link2 className="w-3.5 h-3.5" /> Connect
        </div>
        <p className="font-mono text-[10px] text-muted-foreground text-center">One key. No engineering required.</p>
      </div>
    </MockWindow>
  );
}

export default function IntegrationsSolution() {
  return (
    <FeaturePage
      accent={ACCENT}
      signupRole="company"
      secondaryCta={DEMO_CTA}
      eyebrow="INTEGRATIONS"
      title="Plug into the stack"
      titleAccent="you already run."
      subtitle="Connect Greenhouse or Lever with a single API key, or import roles straight from Workday — pull your candidates in, screen them with AI, and keep working in the tools your team already knows."
      heroChips={["Greenhouse · Lever · Workday", "Connect with one key", "No engineering required"]}
      heroVisual={<AtsVisual />}
      rows={[
        {
          tag: "CONNECT IN MINUTES",
          title: "One API key. That's the whole setup.",
          body: "Paste your Greenhouse Harvest key or Lever API key and you're connected — no developer, no middleware, no lengthy integration project. Workday roles come in via straightforward URL import.",
          bullets: [
            "Greenhouse & Lever via API key",
            "Workday via URL import",
            "No engineering or middleware",
          ],
          visual: <ConnectVisual />,
        },
        {
          tag: "PULL CANDIDATES IN",
          title: "Bring your existing candidates to AI screening.",
          body: "Sync candidates from your ATS instead of re-entering them by hand. Pull a role's applicants across, invite them to an AI interview, and get a ranked shortlist — without leaving your existing workflow behind.",
          bullets: [
            "Import candidates from your ATS",
            "Screen them with AI in one place",
            "No duplicate data entry",
          ],
          visual: <SyncVisual />,
          reverse: true,
        },
        {
          tag: "FITS YOUR WORKFLOW",
          title: "Add AI screening without ripping anything out.",
          body: "You don't have to switch systems to get AI-scored interviews. VetraHub layers on top of the ATS you already use, so your team keeps its process and simply gains a faster, smarter screening step.",
          bullets: [
            "Works alongside your current ATS",
            "No rip-and-replace migration",
            "Your team keeps its workflow",
          ],
          visual: <AtsVisual />,
        },
      ]}
      steps={[
        { title: "Add your key", body: "Connect Greenhouse or Lever with an API key, or set up Workday URL import." },
        { title: "Sync candidates", body: "Pull applicants from your ATS into VetraHub." },
        { title: "Screen with AI", body: "Invite them to AI interviews and get a ranked shortlist." },
      ]}
      useCases={[
        { icon: Workflow, persona: "Teams on Greenhouse/Lever", body: "Add AI screening to the ATS you already live in, with a one-key connection." },
        { icon: Import, persona: "Workday shops", body: "Import roles by URL and start screening without a heavy integration." },
        { icon: RefreshCw, persona: "Agencies", body: "Pull client candidates in, screen at scale, and keep everything in sync." },
      ]}
      faqs={[
        { q: "Which ATS platforms are supported?", a: "Greenhouse and Lever connect with an API key, and Workday roles can be brought in via URL import. More can be added as needs grow." },
        { q: "How hard is it to connect?", a: "For Greenhouse and Lever, you paste one API key — no developer or middleware required. Workday uses a straightforward URL import on the New Role page." },
        { q: "Do I have to leave my current ATS?", a: "No. VetraHub layers on top of your existing ATS, adding AI screening as a step rather than replacing your system of record." },
        { q: "What data comes across?", a: "You can pull a role's candidates in to screen them with AI, so you're not re-entering applicants by hand." },
        { q: "Where do I set this up?", a: "In your account's Integrations settings — connect a platform there and start syncing." },
      ]}
      ctaTitle="Works with what you've got."
      ctaSubtitle="Start free, or book a demo to connect your ATS."
      related={siblingSolutions("integrations")}
      seoTitle="ATS Integrations — Greenhouse, Lever & Workday | VetraHub"
      seoDescription="Connect Greenhouse or Lever with one API key, or import roles from Workday. Pull candidates in, screen them with AI, and keep working in your existing ATS."
    />
  );
}
